import './ListSidebar.scss'

import React, { useState, useCallback } from 'react'
import { useSelector, useDispatch } from 'react-redux'

import { Icon } from 'hb4e-ui-core-ui'
import { CoreConstants, Page } from 'hb4e-ui-core'
import { selectors as authenticationSelectors } from 'hb4e-ui-authentication'
import { List, ListType, ListID } from 'hb4e-ui-context'

import ListSidebarItem from './ListSidebarItem/ListSidebarItem'
import { selectPersonalLists, selectMyLists } from '../../../../reducers'
import { fetchAdditionalLists } from '../../../../actions'
import { urlListView } from '../../../../urls'

const { PERM_CREATE_TRIAL, PERM_ADD_TO_PERSONAL_CART } = CoreConstants

const DEFAULT_SHOWN_LISTS_NUMBER = 20
const DEFAULT_SHOWN_PERSONAL_LISTS_NUMBER = 20
const DEFAULT_STEP_LIMIT = 20

const STATIC_LISTS_ICONS = {
  [ListID.SAVED_ITEMS]: Icon.Bookmark,
  [ListID.PURCHASED_ITEMS]: Icon.CartShopping,
  [ListID.FREE_TRIALS]: Icon.Copy,
}

const ListSidebar = ({ activeList, className = '' }) => {
  const dispatch = useDispatch()

  const [shouldShowAll, setShouldShowAll] = useState(false)
  const [hasMore, setHasMore] = useState(true)

  const user = useSelector(authenticationSelectors.selectUser)
  const personalLists = useSelector((state) => selectPersonalLists(state))
  const { totalLists, lists, nextOffset } = useSelector(selectMyLists)

  const personalListsToDisplay = shouldShowAll
    ? personalLists
    : personalLists.slice(0, DEFAULT_SHOWN_PERSONAL_LISTS_NUMBER)
  const shouldShowFreeTrials = user.hasPermission(PERM_CREATE_TRIAL)
  const shouldShowMyPurchasedItems = user.hasPermission(PERM_ADD_TO_PERSONAL_CART)
  const fetchedListCount = lists.length

  const fetchMoreLists = useCallback(() => {
    if (totalLists === fetchedListCount) {
      setShouldShowAll(!shouldShowAll)
      setHasMore(!hasMore)
      return
    }

    if (totalLists > fetchedListCount) {
      dispatch(fetchAdditionalLists(new Page({ offset: nextOffset, limit: DEFAULT_STEP_LIMIT })))
      setShouldShowAll(true)
    }
  }, [fetchedListCount, dispatch, totalLists, shouldShowAll, hasMore, nextOffset])

  const userPermissionsListFilter = useCallback(
    (list) => {
      switch (list.getType()) {
        case ListType.FREE_TRIALS:
          return shouldShowFreeTrials
        case ListType.PURCHASED_ITEMS:
          return shouldShowMyPurchasedItems
        default:
          return true
      }
    },
    [shouldShowFreeTrials, shouldShowMyPurchasedItems],
  )

  const onShowButtonClick = () => {
    fetchMoreLists()
  }

  const renderLabel = () => {
    if ((totalLists === fetchedListCount) === hasMore) {
      return 'Show less'
    }
    return 'Show more'
  }

  return (
    <div className={`list-sidebar ${className}`}>
      <ul>
        {List.getStaticLists()
          .filter(userPermissionsListFilter)
          .map((list) => (
            <ListSidebarItem
              isActive={list.getID() === activeList}
              IconComponent={STATIC_LISTS_ICONS[list.getID()]}
              key={list.getID()}
              linkTo={urlListView(list.getID())}
              name={list.getName()}
            />
          ))}
      </ul>
      <div className="list-sidebar__list-divider" />
      <ul>
        {personalListsToDisplay.map((list) => (
          <ListSidebarItem
            isActive={list.getID() === activeList}
            key={list.getID()}
            linkTo={urlListView(list.getID())}
            name={list.getName()}
          />
        ))}
      </ul>

      {totalLists > DEFAULT_SHOWN_LISTS_NUMBER && (
        <button className="list-sidebar__show-button" onClick={onShowButtonClick}>
          {renderLabel()}
        </button>
      )}
    </div>
  )
}

export default ListSidebar
